import { tach } from './duong-dan-cong';

/**
 * Bản đồ đường dẫn → mã phân hệ RBAC (§3.4).
 *
 * Khớp theo tiền tố dài nhất. Route không có mặt ở đây thì **mở** cho mọi người
 * đã đăng nhập (§6.4).
 *
 * Bảng khai **đường dẫn gốc**, không mang tiền tố cổng: `moduleForPath` gỡ
 * tiền tố trước khi khớp. Nhờ vậy `/client/tri-thuc` và `/tri-thuc` ra cùng một
 * mã, và thêm cổng mới không phải nhân đôi bảng này (ADR-UI-0012).
 */
const ROUTE_MODULE: ReadonlyArray<readonly [string, string]> = [
  // Tổng quan
  ['/bao-cao-doanh-nghiep/tai-lieu', 'BIZ_REPORT_DOC'],
  ['/bao-cao-doanh-nghiep/nguon', 'ROLE_CONFIG'],
  ['/bao-cao-doanh-nghiep/tong-quan', 'BIZ_OVERVIEW'],
  ['/bao-cao-doanh-nghiep/tai-chinh', 'BIZ_FINANCE'],
  ['/bao-cao-doanh-nghiep/du-an', 'BIZ_PROJECT'],
  ['/bao-cao-doanh-nghiep/kinh-doanh', 'BIZ_SALES'],
  ['/bao-cao-doanh-nghiep/nhan-su', 'BIZ_HR'],
  ['/bao-cao', 'REPORTS'],
  ['/token-ai', 'AI_TOKEN'],
  ['/thong-bao', 'NOTIFICATIONS'],

  // Cấu hình hệ thống
  ['/he-thong/vai-tro', 'ROLE_CONFIG'],
  ['/he-thong/phan-he', 'ROLE_CONFIG'],
  ['/he-thong/nguoi-dung', 'USER_CONFIG'],
  ['/he-thong/to-chuc', 'ORG_STRUCTURE'],
  ['/he-thong/bao-mat', 'SECURITY_CONFIG'],
  ['/he-thong/khoa-api', 'API_KEY'],
  // ADR-0097 mục 2: cùng mã với Cài đặt bảo mật — chỉ quản trị tổ chức gán domain thương hiệu.
  ['/he-thong/dia-chi-rieng', 'SECURITY_CONFIG'],
  // ADR-0102: cùng mã với Cài đặt bảo mật — chỉ quản trị tổ chức cấu hình cảnh báo.
  ['/he-thong/canh-bao', 'SECURITY_CONFIG'],
  ['/he-thong/xu-ly-van-ban', 'TEXT_CONFIG'],
  ['/he-thong/ocr', 'OCR_CONFIG'],

  // Tri thức
  ['/tri-thuc/kiem-duyet', 'KNOWLEDGE_REVIEW'],
  ['/tri-thuc/upload', 'KNOWLEDGE_UPLOAD'],
  ['/tri-thuc/ket-noi', 'DATA_AUTO_SYNC'],
  ['/tri-thuc/ocr-chuan-hoa', 'OCR_NORMALIZE'],
  ['/tri-thuc', 'KNOWLEDGE_MGMT'],

  // Xử lý tài liệu
  ['/xu-ly/nhan-dang', 'OCR_RECOGNIZE'],
  ['/xu-ly/tai-lieu-ocr', 'OCR_FILES'],
  ['/xu-ly/chung-tu', 'DOC_MGMT'],
  ['/xu-ly/hoa-don-vat', 'OCR_RECOGNIZE'],
  ['/xu-ly/hop-dong', 'OCR_RECOGNIZE'],
  ['/xu-ly/phieu-nhap-kho', 'OCR_RECOGNIZE'],
  // Cấu hình nằm trong nhánh của dịch vụ, để gói của tenant quyết định cả màn cấu hình (ADR-UI-0015).
  ['/xu-ly/cau-hinh-ocr', 'OCR_CONFIG'],

  // Xử lý văn bản
  ['/xu-ly-van-ban/tom-tat', 'TEXT_SUMMARIZE'],
  ['/xu-ly-van-ban/soan-thao', 'TEXT_COMPOSE'],
  ['/xu-ly-van-ban/phan-cong', 'TASK_ASSIGN'],
  ['/xu-ly-van-ban/quan-ly-nhiem-vu', 'TASK_MGMT'],
  ['/xu-ly-van-ban/cau-hinh', 'TEXT_CONFIG'],

  // Trợ lí AI
  ['/tro-ly-ai/tong-quan', 'ASSISTANT_DASHBOARD'],
  ['/tro-ly-ai/chatbot', 'ASSISTANT_CHATBOT'],
  ['/tro-ly-ai/ket-noi', 'ASSISTANT_SOURCE'],
  ['/tro-ly-ai/ban-sao', 'ASSISTANT_SOURCE'],
  ['/tro-ly-ai/cong-cu-api', 'ASSISTANT_SOURCE'],
  ['/tro-ly-ai/khuon', 'ASSISTANT_SOURCE'],
  // Màn trò chuyện với một bot (`/tro-ly-ai/tro-chuyen/:botId`): quyền DÙNG bot là quyền hỏi, không phải quyền
  // SỬA bot (`ASSISTANT_CHATBOT`) — từng bot còn được module lọc theo ma trận quyền bot (404 cho bot không thấy).
  ['/tro-ly-ai/tro-chuyen', 'ASSISTANT_ASK'],
  ['/tro-ly-ai/quyen-du-lieu', 'ASSISTANT_DATAPERM'],
  ['/tro-ly-ai/nhat-ky', 'ASSISTANT_TRACE'],
  ['/tro-ly-ai/thu-nghiem', 'ASSISTANT_TRACE'],
  ['/tro-ly-ai', 'ASSISTANT_ASK'],

  // Thẩm định
  ['/tham-dinh/ho-so', 'REVIEW_RUN'],
  ['/tham-dinh/phe-duyet', 'REVIEW_APPROVE'],
  ['/tham-dinh/tieu-chi', 'REVIEW_CRITERIA'],
  ['/tham-dinh/tich-hop', 'REVIEW_CATEGORY'],
  ['/tham-dinh', 'REVIEW_DASHBOARD'],

  // eKYC
  ['/ekyc/dinh-danh', 'EKYC_SESSION'],
  ['/ekyc/ho-so', 'EKYC_SESSION'],
  ['/ekyc/quy-trinh', 'EKYC_WORKFLOW'],
  ['/ekyc/ung-dung', 'EKYC_APP'],
  ['/ekyc', 'EKYC_DASHBOARD'],
];

/**
 * Phân hệ **không gác route nào**, nhưng vẫn phải được cấp theo dịch vụ.
 *
 * `ROUTE_MODULE` ở trên trả lời "đường dẫn này đòi phân hệ gì"; bảng này trả lời "dịch vụ này còn
 * mang phân hệ nào nữa". Hai câu hỏi khác nhau, và gộp lại thì một phân hệ chỉ gác **nút** trong
 * trang sẽ phải bịa ra một đường dẫn giả để tồn tại.
 *
 * `EKYC_REVIEW` là trường hợp đầu tiên: nó là **module riêng**, không phải một action của
 * `EKYC_SESSION` (`docs/eKYC/06_TICH_HOP.md` §1.8). Cùng một màn hồ sơ, người được xem hồ sơ và
 * người được mở **ảnh mặt thật cùng số giấy tờ** là hai vai khác nhau — đó là ranh giới mà
 * `EKYC_REVIEW` được tách ra để giữ, nên nó gác nút chứ không gác trang.
 *
 * ⚠️ Thiếu dòng này thì `tinhQuyenCongTenant` không bao giờ sinh ra `EKYC_REVIEW.*`, và vùng ảnh
 * cùng dữ liệu cá nhân ở màn thẩm định **ẩn vĩnh viễn với mọi tài khoản** — kể cả quản trị tổ
 * chức. Lỗi im lặng: màn vẫn mở, chỉ là không ai xem được ảnh, và không có gì nói vì sao.
 */
const PHAN_HE_KHONG_GAC_ROUTE: ReadonlyArray<readonly [string, string]> = [['/ekyc', 'EKYC_REVIEW']];

/**
 * Mọi cặp tiền tố → mã phân hệ của cổng tenant, kể cả `DASHBOARD` của trang gốc.
 *
 * `lib/dich-vu-cong.ts` đọc bảng này để biết phân hệ nào nằm trong dịch vụ
 * nào, rồi cấp quyền theo gói của tenant (ADR-0034).
 */
export const TIEN_TO_PHAN_HE: ReadonlyArray<readonly [string, string]> = [
  ['/', 'DASHBOARD'],
  ...ROUTE_MODULE,
  ...PHAN_HE_KHONG_GAC_ROUTE,
];

/**
 * Giải đường dẫn thành mã phân hệ, hoặc `undefined` nếu route không được gán.
 *
 * ⚠️ Không route `/rpa/*` nào có mã phân hệ, nên `PermissionGuard` để ngỏ cho
 * mọi người đã đăng nhập. Đúng với bản gốc, nhưng nên nêu lại với người phụ
 * trách (§17.1).
 */
export function moduleForPath(pathname: string): string | undefined {
  // Gỡ tiền tố cổng: bảng dưới khai đường dẫn gốc.
  const { goc } = tach(pathname);
  if (goc === '/') return 'DASHBOARD';

  // Khớp tiền tố dài nhất trước, để `/tri-thuc/upload` không bị `/tri-thuc` chiếm
  // sai phân hệ, và `/tro-ly-ai/nhat-ky` không rơi về `/tro-ly-ai`.
  let best: { prefix: string; module: string } | undefined;
  for (const [prefix, module] of ROUTE_MODULE) {
    if (goc === prefix || goc.startsWith(`${prefix}/`)) {
      if (!best || prefix.length > best.prefix.length) best = { prefix, module };
    }
  }
  return best?.module;
}
